// Chemin: C:\gestion-copro\src\pages\admin\DashboardPage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Dashboard principal pour ORION PILOT
// Session 17 : Ajout compteurs affaires + derniers documents + icônes Lucide
// Session 71 : Ajout widget "Actions en attente" avec useAlertes
// Session 79 : Ajout widget quota SENTINELLE (en haut) + compactage inscriptions
// Session 82 : Bouton +100 analyses fonctionnel avec modale de confirmation
// Session 89 : Intégration widget MandatSuiviSection (Gouvernance V2)
// Session 94 : Ajout widget Prochaines échéances (module Calendrier)

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, getDocs, where, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Signalement } from '../../types/signalement.types';
import { Copropriete } from '../../types/copropriete.types';
import { StatutAffaire } from '../../types/affaire.types';
import { QuotaStats } from '../../types/quota.types';
import { generateSignalementsPDF } from '../../services/pdf.service';
import { getCopropriete, joursAvantExpiration, ajouterQuotaSentinelle } from '../../services/copropriete.service';
import { countAffairesByStatut } from '../../services/affaire.service';
import { getQuotaStats, getCouleurQuota, getCouleurTexteQuota, getLibelleFormule, formaterDateReset } from '../../services/quota.service';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAlertes } from '../../hooks/useAlertes';
import PendingUsersSection from '../../components/admin/PendingUsersSection';
import MandatSuiviSection from '../../components/mandat/MandatSuiviSection'; // Session 89
import EcheancesWidget from '../../components/calendrier/EcheancesWidget'; // Session 94
import {
  ClipboardList,
  AlertCircle,
  Clock,
  CheckCircle,
  Briefcase,
  FolderOpen,
  FolderClock,
  FolderCheck,
  FileText,
  Building2,
  Plus,
  Download,
  AlertTriangle,
  Mail,
  Calculator,
  Bell,
  ArrowRight,
  Loader2,
  Sparkles,
  ShoppingCart,
  X
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface Stats {
  total: number;
  nouveaux: number;
  enCours: number;
  resolus: number;
}

interface RecentDocument {
  id: string;
  titre: string;
  type: string;
  createdAt: any;
  fichierUrl: string;
}

// ============================================
// COMPOSANTS UTILITAIRES
// ============================================

// Carte de statistique
const StatCard: React.FC<{
  titre: string;
  valeur: number;
  icon: React.ReactNode;
  couleur: string;
  lien?: string;
}> = ({ titre, valeur, icon, couleur, lien }) => {
  const content = (
    <div className={`bg-white rounded-xl shadow-sm p-6 border-l-4 ${couleur} hover:shadow-md transition-shadow`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{titre}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{valeur}</p>
        </div>
        <div className="text-gray-400 opacity-80">{icon}</div>
      </div>
    </div>
  );

  if (lien) {
    return <Link to={lien}>{content}</Link>;
  }
  return content;
};

// Badge de statut
const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    nouveau: { bg: 'bg-red-100', text: 'text-red-700', label: 'Nouveau' },
    en_cours: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En cours' },
    transmis_syndic: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Transmis syndic' },
    transmis_promoteur: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Transmis promoteur' },
    resolu: { bg: 'bg-green-100', text: 'text-green-700', label: 'Résolu' },
    refuse: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Refusé' },
  };

  const { bg, text, label } = config[statut] || config.nouveau;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
};

// Badge d'urgence
const UrgenceBadge: React.FC<{ urgence: string }> = ({ urgence }) => {
  const config: Record<string, { color: string }> = {
    basse: { color: 'text-green-500' },
    moyenne: { color: 'text-yellow-500' },
    haute: { color: 'text-orange-500' },
    critique: { color: 'text-red-500' },
  };

  const { color } = config[urgence] || config.basse;

  return <span className={`${color} text-lg`}>●</span>;
};

// SESSION 79 : Widget Quota SENTINELLE (version compacte horizontale)
// SESSION 82 : Ajout fonctionnalité achat +100 analyses via Stripe Payment Link
const QuotaWidgetCompact: React.FC<{ 
  quotaStats: QuotaStats | null; 
  loading: boolean;
  coproprieteId: string | undefined;
  onQuotaUpdated: () => void;
}> = ({ quotaStats, loading, coproprieteId, onQuotaUpdated }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Session 82 : Redirection vers Stripe Payment Link pour achat +100 analyses
  const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/aFa00dgiMdXj0hecaXgfu0c';

  // Fonction d'achat de +100 analyses - Redirection Stripe
  const handlePurchase = async () => {
    // Ouvrir le Payment Link Stripe dans un nouvel onglet
    window.open(STRIPE_PAYMENT_LINK, '_blank');
    // Fermer la modale
    setShowConfirmModal(false);
  };

  if (loading) {
    return (
      <div className="mb-6 bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-blue-500" />
          <span className="font-semibold text-gray-800">Quota SENTINELLE</span>
          <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-2" />
        </div>
      </div>
    );
  }

  if (!quotaStats) {
    return null; // Ne rien afficher si pas de quota configuré
  }

  return (
    <>
      <div className={`mb-6 rounded-xl shadow-sm overflow-hidden ${
        quotaStats.isAlerte100 ? 'bg-red-50 border border-red-200' :
        quotaStats.isAlerte80 ? 'bg-amber-50 border border-amber-200' :
        'bg-white'
      }`}>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Titre et formule */}
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                quotaStats.isAlerte100 ? 'bg-red-100' :
                quotaStats.isAlerte80 ? 'bg-amber-100' :
                'bg-blue-100'
              }`}>
                <Sparkles className={`w-5 h-5 ${
                  quotaStats.isAlerte100 ? 'text-red-600' :
                  quotaStats.isAlerte80 ? 'text-amber-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">Quota SENTINELLE</span>
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {getLibelleFormule(quotaStats.formule)}
                  </span>
                </div>
                <p className={`text-sm ${getCouleurTexteQuota(quotaStats.pourcentage)}`}>
                  {quotaStats.usage} / {quotaStats.quota} analyses utilisées
                  {quotaStats.resetDate && (
                    <span className="text-gray-400 ml-2">
                      · Renouvellement le {formaterDateReset(quotaStats.resetDate)}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Barre de progression */}
            <div className="flex-1 sm:max-w-xs">
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getCouleurQuota(quotaStats.pourcentage)}`}
                    style={{ width: `${Math.min(quotaStats.pourcentage, 100)}%` }}
                  />
                </div>
                <span className={`text-sm font-semibold min-w-[3rem] text-right ${getCouleurTexteQuota(quotaStats.pourcentage)}`}>
                  {quotaStats.pourcentage}%
                </span>
              </div>
            </div>

            {/* Bouton achat si alerte */}
            {quotaStats.isAlerte80 && (
              <button
                onClick={() => setShowConfirmModal(true)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  quotaStats.isAlerte100 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
              >
                <ShoppingCart className="w-4 h-4" />
                +100 analyses
              </button>
            )}
          </div>

          {/* Message d'alerte si quota épuisé */}
          {quotaStats.isAlerte100 && (
            <div className="mt-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Quota épuisé — Les analyses SENTINELLE sont suspendues</span>
            </div>
          )}
        </div>
      </div>

      {/* Modale de confirmation d'achat */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Recharger le quota
              </h2>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700">Pack supplémentaire</span>
                  <span className="font-bold text-blue-700">+100 analyses</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Prix TTC</span>
                  <span className="font-bold text-gray-900">34,80 €</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>(soit 29 € HT)</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Après achat :</strong><br />
                  Quota actuel : {quotaStats.quota} → <span className="text-green-600 font-semibold">{quotaStats.quota + 100} analyses</span>
                </p>
              </div>

              <p className="text-xs text-gray-500 mb-4">
                Vous allez être redirigé vers notre partenaire de paiement sécurisé Stripe. 
                Votre quota sera mis à jour après validation du paiement.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePurchase}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                Payer 34,80 €
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const DashboardPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { coproprieteId } = useCopropriete();
  const [stats, setStats] = useState<Stats>({ total: 0, nouveaux: 0, enCours: 0, resolus: 0 });
  const [statsAffaires, setStatsAffaires] = useState<Record<StatutAffaire, number>>({
    ouverte: 0,
    en_cours: 0,
    en_attente: 0,
    cloturee: 0
  });
  const [recentSignalements, setRecentSignalements] = useState<Signalement[]>([]);
  const [recentDocuments, setRecentDocuments] = useState<RecentDocument[]>([]);
  const [allSignalements, setAllSignalements] = useState<Signalement[]>([]);
  const [copropriete, setCopropriete] = useState<Copropriete | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Session 79 : État pour le quota SENTINELLE
  const [quotaStats, setQuotaStats] = useState<QuotaStats | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);

  // SESSION 71 : Hook alertes pour widget "Actions en attente"
  const { alertes, loading: alertesLoading } = useAlertes();

  // Charger les données
  useEffect(() => {
    const loadData = async () => {
      try {
        // Charger la copropriété si l'utilisateur en a une
        if (coproprieteId) {
          const coproData = await getCopropriete(coproprieteId);
          setCopropriete(coproData);

          // Charger les stats des affaires
          const affairesStats = await countAffairesByStatut(coproprieteId);
          setStatsAffaires(affairesStats);

          // Session 79 : Charger le quota SENTINELLE
          setQuotaLoading(true);
          const quota = await getQuotaStats(coproprieteId);
          setQuotaStats(quota);
          setQuotaLoading(false);

          // Charger les derniers documents
          try {
            const docsQuery = query(
              collection(db, 'documents'),
              where('coproprieteId', '==', coproprieteId),
              orderBy('createdAt', 'desc'),
              limit(5)
            );
            const docsSnapshot = await getDocs(docsQuery);
            const docsData: RecentDocument[] = [];
            docsSnapshot.forEach((doc) => {
              const data = doc.data();
              docsData.push({
                id: doc.id,
                titre: data.titre,
                type: data.type,
                createdAt: data.createdAt,
                fichierUrl: data.fichierUrl
              });
            });
            setRecentDocuments(docsData);
          } catch (docError) {
            // Si erreur d'index, on continue sans les documents
            console.warn('Erreur chargement documents récents:', docError);
          }
        }

        // Charger les signalements
        const signalementsRef = collection(db, 'signalements');
        const allQuery = query(signalementsRef, orderBy('dateSignalement', 'desc'));
        const allSnapshot = await getDocs(allQuery);
        
        let total = 0;
        let nouveaux = 0;
        let enCours = 0;
        let resolus = 0;
        const allData: Signalement[] = [];

        allSnapshot.forEach((doc) => {
          const data = doc.data();
          const signalement = { id: doc.id, ...data } as Signalement;
          allData.push(signalement);
          
          total++;
          switch (data.statut) {
            case 'nouveau':
              nouveaux++;
              break;
            case 'en_cours':
            case 'transmis_syndic':
            case 'transmis_promoteur':
              enCours++;
              break;
            case 'resolu':
              resolus++;
              break;
          }
        });

        setStats({ total, nouveaux, enCours, resolus });
        setAllSignalements(allData);
        setRecentSignalements(allData.slice(0, 5));
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [coproprieteId]);

  // Formater la date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Format date court
  const formatDateShort = (timestamp: any): string => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  // Temps relatif
  const formatRelativeTime = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "À l'instant";
    if (hours < 24) return `Il y a ${hours}h`;
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;
    return formatDate(timestamp);
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (allSignalements.length === 0) {
      alert('Aucun signalement à exporter');
      return;
    }

    setExportingPDF(true);
    try {
      const nomCopro = copropriete?.nom || 'Copropriété';
      const villeCopro = copropriete?.adresse?.ville || '';
      await generateSignalementsPDF(allSignalements, {
        titre: 'Synthèse des Signalements',
        sousTitre: `${nomCopro}${villeCopro ? ' - ' + villeCopro : ''}`
      });
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  // Calcul des jours restants GPA
  const joursRestantsGPA = copropriete?.dateExpirationGPA 
    ? joursAvantExpiration(copropriete.dateExpirationGPA) 
    : null;

  // Nombre total d'affaires actives (ouvertes + en cours)
  const affairesActives = statsAffaires.ouverte + statsAffaires.en_cours;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <>
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Tableau de bord</h1>
          <p className="text-gray-500">
            {userProfile?.isGestionnaireSaas && copropriete ? (
              <>Administrateur Copro Protect — Espace CS de <strong>{copropriete.nom}</strong></>
            ) : (
              copropriete ? copropriete.nom : 'Bienvenue sur ORION PILOT'
            )}
          </p>
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exportingPDF || allSignalements.length === 0}
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
            ${exportingPDF || allSignalements.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-900 text-white hover:bg-blue-800 hover:shadow-lg'
            }
          `}
        >
          {exportingPDF ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Génération...</span>
            </>
          ) : (
            <>
              <Download size={18} />
              <span>Exporter PDF</span>
            </>
          )}
        </button>
      </div>

      {/* SESSION 79 : Widget Quota SENTINELLE (en haut, compact) */}
      {/* SESSION 82 : Ajout props pour fonctionnalité achat +100 */}
      <QuotaWidgetCompact 
        quotaStats={quotaStats} 
        loading={quotaLoading}
        coproprieteId={coproprieteId ?? undefined}
        onQuotaUpdated={async () => {
          if (coproprieteId) {
            setQuotaLoading(true);
            const quota = await getQuotaStats(coproprieteId);
            setQuotaStats(quota);
            setQuotaLoading(false);
          }
        }}
      />

      {/* SESSION 89 : Widget suivi mandat de coordination (Gouvernance V2) */}
      <MandatSuiviSection />

      {/* Section inscriptions en attente - VERSION COMPACTE */}
      <PendingUsersSection />

      {/* SESSION 94 : Widget prochaines échéances du calendrier */}
      <EcheancesWidget />

      {/* Alerte GPA - Affichée uniquement si données disponibles */}
      {joursRestantsGPA !== null && copropriete?.dateExpirationGPA && (
        <div className={`mb-6 p-4 rounded-xl border-l-4 ${
          joursRestantsGPA < 90 ? 'bg-red-50 border-red-500' :
          joursRestantsGPA < 180 ? 'bg-orange-50 border-orange-500' :
          'bg-blue-50 border-blue-500'
        }`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-6 h-6 ${
              joursRestantsGPA < 90 ? 'text-red-500' :
              joursRestantsGPA < 180 ? 'text-orange-500' :
              'text-blue-500'
            }`} />
            <div>
              <p className="font-semibold text-gray-800">
                Garantie de Parfait Achèvement (GPA)
              </p>
              <p className="text-gray-600">
                {joursRestantsGPA > 0 ? (
                  <>
                    Expiration dans <strong>{joursRestantsGPA} jours</strong>
                    <span className="text-gray-400 ml-2">
                      ({formatDate(copropriete.dateExpirationGPA)})
                    </span>
                  </>
                ) : (
                  <span className="text-red-600 font-semibold">Garantie expirée</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SESSION 71 : Widget Actions en attente */}
      {!alertesLoading && alertes.total > 0 && (
        <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 overflow-hidden">
          <div className="p-4 border-b border-amber-200 bg-amber-100/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-amber-900">Actions en attente</h2>
                <p className="text-sm text-amber-700">{alertes.total} élément{alertes.total > 1 ? 's' : ''} nécessite{alertes.total > 1 ? 'nt' : ''} votre attention</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {/* Courriers en attente */}
            {alertes.courriers > 0 && (
              <Link
                to="/admin/courriers"
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Mail className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Courriers à traiter</p>
                    <p className="text-sm text-gray-500">Brouillons ou en attente de validation</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    {alertes.courriers}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
                </div>
              </Link>
            )}

            {/* Messagerie en attente */}
            {alertes.messagerie > 0 && (
              <Link
                to="/admin/messagerie"
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Messages en attente de vote</p>
                    <p className="text-sm text-gray-500">Validez ou refusez les messages du CS</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {alertes.messagerie}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
              </Link>
            )}

            {/* Comptabilité - anomalies */}
            {alertes.comptabilite > 0 && (
              <Link
                to="/admin/comptabilite"
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Calculator className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Anomalies comptables</p>
                    <p className="text-sm text-gray-500">Contrôles à traiter ou vérifier</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                    {alertes.comptabilite}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 transition-colors" />
                </div>
              </Link>
            )}

            {/* Signalements nouveaux */}
            {alertes.signalements > 0 && (
              <Link
                to="/admin/signalements?statut=nouveau"
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ClipboardList className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Nouveaux signalements</p>
                    <p className="text-sm text-gray-500">À affecter à une affaire</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                    {alertes.signalements}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                </div>
              </Link>
            )}

            {/* Affaires urgentes */}
            {alertes.affaires > 0 && (
              <Link
                to="/admin/affaires"
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Affaires prioritaires</p>
                    <p className="text-sm text-gray-500">Urgentes ou garantie expirant bientôt</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {alertes.affaires}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                </div>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Cartes statistiques - Signalements */}
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Signalements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            titre="Total"
            valeur={stats.total}
            icon={<ClipboardList size={32} />}
            couleur="border-blue-500"
            lien="/admin/signalements"
          />
          <StatCard
            titre="Nouveaux"
            valeur={stats.nouveaux}
            icon={<AlertCircle size={32} />}
            couleur="border-red-500"
            lien="/admin/signalements?statut=nouveau"
          />
          <StatCard
            titre="En cours"
            valeur={stats.enCours}
            icon={<Clock size={32} />}
            couleur="border-yellow-500"
            lien="/admin/signalements?statut=en_cours"
          />
          <StatCard
            titre="Résolus"
            valeur={stats.resolus}
            icon={<CheckCircle size={32} />}
            couleur="border-green-500"
            lien="/admin/signalements?statut=resolu"
          />
        </div>
      </div>

      {/* Cartes statistiques - Affaires */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Affaires & Dossiers</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            titre="Affaires actives"
            valeur={affairesActives}
            icon={<Briefcase size={32} />}
            couleur="border-purple-500"
            lien="/admin/affaires"
          />
          <StatCard
            titre="Ouvertes"
            valeur={statsAffaires.ouverte}
            icon={<FolderOpen size={32} />}
            couleur="border-blue-500"
            lien="/admin/affaires"
          />
          <StatCard
            titre="En cours"
            valeur={statsAffaires.en_cours}
            icon={<FolderClock size={32} />}
            couleur="border-yellow-500"
            lien="/admin/affaires"
          />
          <StatCard
            titre="Clôturées"
            valeur={statsAffaires.cloturee}
            icon={<FolderCheck size={32} />}
            couleur="border-green-500"
            lien="/admin/affaires"
          />
        </div>
      </div>

      {/* Trois colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signalements récents */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Signalements récents</h2>
            <Link 
              to="/admin/signalements"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Voir tout
            </Link>
          </div>
          
          {recentSignalements.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Aucun signalement</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentSignalements.map((sig) => (
                <Link
                  key={sig.id}
                  to={`/admin/signalements/${sig.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <UrgenceBadge urgence={sig.urgence} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate text-sm">{sig.titre}</p>
                      <p className="text-xs text-gray-500">
                        {formatRelativeTime(sig.dateSignalement)}
                      </p>
                    </div>
                    <StatutBadge statut={sig.statut} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Documents récents */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Documents récents</h2>
            <Link 
              to="/admin/documents"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Voir tout
            </Link>
          </div>
          
          {recentDocuments.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Aucun document</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentDocuments.map((doc) => (
                <a
                  key={doc.id}
                  href={doc.fichierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded flex items-center justify-center">
                      <FileText className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate text-sm">{doc.titre}</p>
                      <p className="text-xs text-gray-500">{formatDateShort(doc.createdAt)}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Actions rapides + Infos copro */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">Actions rapides</h2>
            </div>
            <div className="p-4 space-y-2">
              <Link
                to="/admin/signalements"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ClipboardList className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Signalements</span>
              </Link>
              <Link
                to="/admin/affaires"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Briefcase className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Affaires</span>
              </Link>
              <Link
                to="/admin/documents"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FileText className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Documents</span>
              </Link>
              <Link
                to="/admin/copropriete"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Building2 className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Copropriété</span>
              </Link>
              <a
                href="/signaler"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Plus className="w-5 h-5 text-gray-500" />
                <span className="text-gray-700">Nouveau signalement</span>
              </a>
            </div>
          </div>

          {/* Informations copropriété - si disponible */}
          {copropriete && (
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">Copropriété</h2>
              </div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Nom</span>
                  <span className="font-medium">{copropriete.nom}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Lots</span>
                  <span className="font-medium">{copropriete.nombreLots || 'Non renseigné'}</span>
                </div>
                {copropriete.dateExpirationGPA && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Fin GPA</span>
                    <span className="font-medium">{formatDate(copropriete.dateExpirationGPA)}</span>
                  </div>
                )}
                <Link
                  to="/admin/copropriete"
                  className="block text-center text-blue-600 hover:text-blue-800 pt-2"
                >
                  Voir la fiche complète
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
